import {
  estadoDeLinea,
  type AsignacionDelPlano,
  EstadoDeLinea,
  LineaDeReceta,
  ReconocimientoDeTipo,
  Receta,
  RecetaRecordada,
} from '@supernova/shared';

/**
 * «Asignar conceptos a elementos»: lógica PURA del diálogo (sin red ni React). Arma y edita la receta de un
 * elemento (UN principal + sus complementarios) y decide en qué estado se ve cada elemento del árbol.
 */

export interface ConceptoElegido {
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
}

/** Lo que se mide de un principal; `null` = automático (lo decide el armado como siempre). */
export type QueSeMide = 'conteo' | 'lineal' | 'area' | 'volumen' | 'peso' | null;

export const RECETA_NO_PRESUPUESTAR: Receta = {
  noPresupuestar: true,
  catalogoAncla: null,
  recuerda: { magnitud: true, partida: false, complementarios: true },
  partida: null,
  agrupacion: null,
  lineas: [],
};

const lineaNueva = (c: ConceptoElegido, orden: number, rol: LineaDeReceta['rol']): LineaDeReceta => ({
  orden,
  rol,
  deOrden: rol === 'principal' ? null : 0,
  catalogId: c.catalogId,
  clave: c.clave,
  unidad: c.unidad,
  descripcion: c.descripcion,
  magnitud: null,
  derivacion: null,
  supuesto: false,
});

const complementosDe = (r: Receta | null): LineaDeReceta[] => (r && !r.noPresupuestar ? r.lineas.filter((l) => l.rol === 'complemento') : []);

/** Pone `c` como concepto principal; los complementarios que ya había se conservan (cuelgan del nuevo principal). */
export function recetaConPrincipal(actual: Receta | null, c: ConceptoElegido): Receta {
  const lineas: LineaDeReceta[] = [lineaNueva(c, 0, 'principal')];
  for (const l of complementosDe(actual)) {
    if (l.catalogId === c.catalogId && l.clave === c.clave) continue;
    lineas.push({ ...l, orden: lineas.length, deOrden: 0 });
  }
  return {
    noPresupuestar: false,
    catalogoAncla: c.catalogId,
    recuerda: { magnitud: true, partida: false, complementarios: true },
    partida: null,
    agrupacion: actual && !actual.noPresupuestar ? actual.agrupacion : null,
    lineas,
  };
}

/** Agrega `c` como complementario del principal; null si todavía no hay principal. Un concepto repetido no se agrega dos veces. */
export function recetaConComplemento(actual: Receta | null, c: ConceptoElegido): Receta | null {
  if (!actual || actual.noPresupuestar || actual.lineas.length === 0) return null;
  if (actual.lineas.some((l) => l.catalogId === c.catalogId && l.clave === c.clave)) return actual;
  return { ...actual, lineas: [...actual.lineas, lineaNueva(c, actual.lineas.length, 'complemento')] };
}

/** Quita una línea. Quitar el principal vacía la receta (null): sin principal no hay receta. */
export function recetaSinLinea(r: Receta, orden: number): Receta | null {
  const linea = r.lineas.find((l) => l.orden === orden);
  if (!linea || linea.rol === 'principal') return null;
  const lineas = r.lineas.filter((l) => l.orden !== orden).map((l, i) => ({ ...l, orden: i, deOrden: l.rol === 'principal' ? null : 0 }));
  return { ...r, lineas };
}

/** Cambia qué se mide del principal (`null` = automático). */
export function recetaConMagnitud(r: Receta, que: QueSeMide): Receta {
  return {
    ...r,
    recuerda: { ...r.recuerda, magnitud: true },
    lineas: r.lineas.map((l) => (l.rol === 'principal' ? { ...l, magnitud: que ? { magnitud: que, inputExtra: 'ninguno' as const } : null } : l)),
  };
}

export function queSeMideDe(r: Receta): QueSeMide {
  const m = r.lineas.find((l) => l.rol === 'principal')?.magnitud?.magnitud;
  return m === 'conteo' || m === 'lineal' || m === 'area' || m === 'volumen' || m === 'peso' ? m : null;
}

/** ¿Dos recetas piden lo mismo (conceptos, rol y qué se mide)? No compara las fotos de descripción. */
export function mismaReceta(a: Receta | null, b: Receta | null): boolean {
  if (!a || !b) return a === b;
  if (a.noPresupuestar !== b.noPresupuestar) return false;
  if (a.lineas.length !== b.lineas.length) return false;
  return a.lineas.every((l, i) => {
    const o = b.lineas[i]!;
    return l.catalogId === o.catalogId && l.clave === o.clave && l.rol === o.rol && l.magnitud?.magnitud === o.magnitud?.magnitud;
  });
}

/** Lo que el servidor dijo de una línea recordada (`RecetaRecordada.lineas[i]`). */
export interface LineaDelServidor {
  estado: EstadoDeLinea;
  resourceId: string | null;
  precio: number | null;
  descripcionActual: string | null;
  unidadActual: string | null;
}

/**
 * El estado de una línea contra los catálogos que la pantalla usa o la persona tiene MARCADOS en el diálogo (aún sin
 * guardar). El servidor juzga con los marcados guardados: si ahí el catálogo no estaba marcado, el estado verdadero
 * (unidad, descripción, precio) quedó tapado y se recalcula con lo que el propio servidor devolvió.
 */
export function estadoLocal(
  l: LineaDelServidor,
  foto: { unidad: string | null; descripcion: string },
  catalogId: string,
  marcados: ReadonlySet<string>,
): EstadoDeLinea {
  if (l.estado === 'catalogo_no_marcado' && marcados.has(catalogId)) {
    return estadoDeLinea(foto, l.resourceId ? { unidad: l.unidadActual, descripcion: l.descripcionActual ?? '', precio: l.precio } : null, { existe: true, marcado: true });
  }
  if (l.estado === 'vigente' && !marcados.has(catalogId)) return 'catalogo_no_marcado';
  return l.estado;
}

/** Lo que se le dice a la persona de una línea que ya no sirve (sin jerga). */
export function textoDeEstado(estado: EstadoDeLinea): string | null {
  switch (estado) {
    case 'vigente':
      return null;
    case 'catalogo_no_disponible':
      return 'El catálogo ya no está disponible.';
    case 'catalogo_no_marcado':
      return 'Su catálogo no está marcado en este proyecto.';
    case 'no_existe':
      return 'Este concepto ya no está en el catálogo.';
    case 'cambio_unidad':
      return 'Cambió la unidad en el catálogo.';
    case 'cambio_descripcion':
      return 'Cambió la descripción en el catálogo.';
    case 'sin_precio':
      return 'No tiene precio en el catálogo.';
  }
}

/** El estado de la línea `i` de una candidata, con los catálogos marcados de la pantalla. */
export const estadoDeCandidata = (c: RecetaRecordada, i: number, marcados: ReadonlySet<string>): EstadoDeLinea => {
  const foto = c.receta.lineas[i]!;
  return estadoLocal(c.lineas[i]!, foto, foto.catalogId, marcados);
};

export const candidataVigente = (c: RecetaRecordada, marcados: ReadonlySet<string>): boolean =>
  c.lineas.every((_l, i) => estadoDeCandidata(c, i, marcados) === 'vigente');

export type PuntoDeEstado = 'reconocido' | 'sugerido' | 'conflicto' | 'elegido' | 'sin' | 'roto';

export interface EstadoDelSujeto {
  punto: PuntoDeEstado;
  /** La receta con la que se armaría hoy (null = el motor de siempre). */
  receta: Receta | null;
  /** Reconocido: la receta recordada que se aplicaría sola. */
  candidata: RecetaRecordada | null;
}

/** Lo decidido en el diálogo para un elemento (`receta: null` = quitar lo asignado en este plano). */
export interface DecisionDelSujeto {
  receta: Receta | null;
  recordar: boolean;
  soloSugerir: boolean;
}

/** Cómo se ve un elemento: lo decidido ahora, lo asignado en este plano, lo recordado (exacto, conflicto, roto o solo sugerido) o nada. */
export function estadoDelSujeto(args: {
  nivel: 'clase' | 'tipo';
  decision: DecisionDelSujeto | undefined;
  asignacion: AsignacionDelPlano | undefined;
  reconocimiento: ReconocimientoDeTipo | null;
  marcados: ReadonlySet<string>;
}): EstadoDelSujeto {
  const { decision, asignacion, reconocimiento, marcados } = args;
  // Quitar lo del plano (`receta: null`) no borra lo recordado: sin lo decidido aquí, vuelve a valer la memoria.
  if (decision?.receta) return { punto: 'elegido', receta: decision.receta, candidata: null };
  if (!decision && asignacion) return { punto: 'elegido', receta: asignacion.receta, candidata: null };
  const candidatas = reconocimiento?.candidatas ?? [];
  if (candidatas.length === 0) return { punto: 'sin', receta: null, candidata: null };
  const exactas = args.nivel === 'tipo' ? candidatas.filter((c) => c.coincidencia === 'exacta' && c.nivel === 'tipo') : [];
  const vigentes = exactas.filter((c) => candidataVigente(c, marcados));
  if (vigentes.length === 1) return { punto: 'reconocido', receta: vigentes[0]!.receta, candidata: vigentes[0]! };
  if (vigentes.length > 1) return { punto: 'conflicto', receta: null, candidata: null };
  // Lo recordado que ya no sirve (cambió, ya no existe, su catálogo no está marcado) no se muestra: el elemento queda sin concepto.
  if (exactas.length > 0) return { punto: 'sin', receta: null, candidata: null };
  return { punto: 'sugerido', receta: null, candidata: null };
}

export type FiltroDelArbol = 'todos' | 'sin' | 'sugeridos' | 'revisar';

export function pasaElFiltro(f: FiltroDelArbol, p: PuntoDeEstado): boolean {
  if (f === 'todos') return true;
  if (f === 'sin') return p === 'sin';
  if (f === 'sugeridos') return p === 'sugerido' || p === 'conflicto';
  return p === 'conflicto';
}

/** Una receta recordada con las fotos al día (para «Usar el texto nuevo»): cada línea toma descripción y unidad de hoy. */
export function recetaConTextoNuevo(c: RecetaRecordada): Receta {
  return {
    ...c.receta,
    lineas: c.receta.lineas.map((l, i) => {
      const e = c.lineas[i];
      if (!e || e.estado === 'no_existe' || e.estado === 'catalogo_no_disponible') return l;
      return { ...l, descripcion: e.descripcionActual ?? l.descripcion, unidad: e.unidadActual ?? l.unidad };
    }),
  };
}
